<script setup>
import { useApi } from '@/composables/useApi'
import { useUserDisplayName } from '@/composables/useUserDisplayName'
import { resolveAnaesthetistName } from '@/utils/resolveAnaesthetistName'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
    roles: ['secretary_anesthetist'],
  },
})

const router = useRouter()
const userName = useUserDisplayName('Anaesthetist secretary')
const anaesthetists = ref([])
const loading = ref(false)
const error = ref('')

const headers = [
  { title: 'Anaesthetist', key: 'name', sortable: true },
  { title: 'GMC number', key: 'gmc_number', sortable: true },
  { title: 'Hospitals', key: 'hospitals', sortable: false },
  { title: 'Specialist areas', key: 'specialist_areas', sortable: false },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Profile', key: 'actions', sortable: false },
]

const resolveRowItem = item => item?.raw ?? item

const resolveStatus = anaesthetist => (anaesthetist?.is_active ? 'Active' : 'Inactive')

const resolveStatusColor = anaesthetist => (anaesthetist?.is_active ? 'success' : 'error')

const resolveHospitalCount = anaesthetist => anaesthetist?.hospitals?.length ?? 0

const resolveAreaCount = anaesthetist => anaesthetist?.specialist_areas?.length ?? 0

const openProfile = anaesthetist => {
  if (!anaesthetist?.id)
    return

  router.push({
    name: 'anaesthetist-id-tab',
    params: {
      id: String(anaesthetist.id),
      tab: 'profile',
    },
  })
}

const fetchAnaesthetists = async () => {
  loading.value = true
  error.value = ''

  try {
    const { data, error: fetchError } = await useApi('/api/secretary-anesthetists/anaesthetists')

    if (fetchError.value)
      throw fetchError.value

    const payload = data.value?.data ?? data.value ?? []

    anaesthetists.value = Array.isArray(payload)
      ? payload
      : (Array.isArray(payload?.data) ? payload.data : [])
  }
  catch (err) {
    error.value = err?.response?._data?.message
      ?? err?.data?.message
      ?? err?.message
      ?? 'Unable to load anaesthetists.'
  }
  finally {
    loading.value = false
  }
}

const stats = computed(() => {
  const total = anaesthetists.value.length
  const active = anaesthetists.value.filter(anaesthetist => anaesthetist?.is_active).length
  const withHospitals = anaesthetists.value.filter(anaesthetist => resolveHospitalCount(anaesthetist) > 0).length
  const withSpecialties = anaesthetists.value.filter(anaesthetist => resolveAreaCount(anaesthetist) > 0).length

  return [
    { label: 'Assigned anaesthetists', value: total, icon: 'ri-user-3-line' },
    { label: 'Active profiles', value: active, icon: 'ri-check-line' },
    { label: 'With hospitals', value: withHospitals, icon: 'ri-hospital-line' },
    { label: 'With specialisms', value: withSpecialties, icon: 'ri-stethoscope-line' },
  ]
})

const previewItems = computed(() => anaesthetists.value.slice(0, 5))

onMounted(fetchAnaesthetists)
</script>

<template>
  <div
    class="secretary-anaesthetist-dashboard"
    data-test-id="secretary-anaesthetist-dashboard"
  >
    <VCard class="mb-4">
      <VCardText>
        <div class="d-flex align-center justify-space-between ga-4">
          <div>
            <h2 class="text-h4 mb-2">
              Welcome, {{ userName }}
            </h2>
            <p class="text-body-2 text-medium-emphasis">
              Manage your assigned anaesthetists and open their profiles for updates.
            </p>
          </div>
          <VIcon
            size="x-large"
            color="primary"
          >
            ri-stethoscope-line
          </VIcon>
        </div>
      </VCardText>
    </VCard>

    <VRow class="mb-4">
      <VCol
        v-for="stat in stats"
        :key="stat.label"
        cols="12"
        sm="6"
        md="3"
      >
        <VCard variant="tonal">
          <VCardText class="d-flex align-center justify-space-between">
            <div>
              <div class="text-h4 font-weight-bold">
                {{ stat.value }}
              </div>
              <div class="text-body-2 text-medium-emphasis">
                {{ stat.label }}
              </div>
            </div>
            <VIcon :icon="stat.icon" />
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardTitle class="d-flex align-center justify-space-between flex-wrap ga-2">
        <div>
          <div class="text-h5">
            Assigned anaesthetists
          </div>
          <div class="text-body-2 text-medium-emphasis mt-1">
            Open a profile and update personal details, hospitals, specialist areas and availability.
          </div>
        </div>

        <div class="d-flex align-center ga-2">
          <VBtn
            variant="tonal"
            prepend-icon="ri-list-check"
            @click="router.push({ name: 'secretary-anesthetist-anaesthetists-page' })"
          >
            Full list
          </VBtn>
          <VBtn
            variant="text"
            prepend-icon="ri-refresh-line"
            :loading="loading"
            @click="fetchAnaesthetists"
          >
            Refresh
          </VBtn>
        </div>
      </VCardTitle>
      <VDivider />
      <VCardText>
        <VAlert
          v-if="error"
          type="error"
          class="mb-4"
        >
          {{ error }}
        </VAlert>

        <VProgressLinear
          v-if="loading"
          indeterminate
          class="mb-4"
        />

        <VDataTable
          :headers="headers"
          :items="previewItems"
          item-key="id"
          class="text-no-wrap"
        >
          <template #item.name="{ item }">
            {{ resolveAnaesthetistName(resolveRowItem(item)) }}
          </template>

          <template #item.gmc_number="{ item }">
            {{ resolveRowItem(item)?.gmc_number || '—' }}
          </template>

          <template #item.hospitals="{ item }">
            <VChip
              size="small"
              variant="tonal"
            >
              {{ resolveHospitalCount(resolveRowItem(item)) }}
            </VChip>
          </template>

          <template #item.specialist_areas="{ item }">
            <VChip
              size="small"
              variant="tonal"
            >
              {{ resolveAreaCount(resolveRowItem(item)) }}
            </VChip>
          </template>

          <template #item.status="{ item }">
            <VChip
              size="small"
              :color="resolveStatusColor(resolveRowItem(item))"
              variant="tonal"
            >
              {{ resolveStatus(resolveRowItem(item)) }}
            </VChip>
          </template>

          <template #item.actions="{ item }">
            <VBtn
              size="small"
              :disabled="!resolveRowItem(item)?.id"
              @click.stop="openProfile(resolveRowItem(item))"
            >
              View profile
            </VBtn>
          </template>

          <template #no-data>
            <div class="text-body-2 text-medium-emphasis">
              No anaesthetists are currently linked to your account.
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
.secretary-anaesthetist-dashboard {
  padding-block: 0.5rem 0;
  padding-inline: 0;
}
</style>
